import React, { FC, useState, useEffect, useRef, useCallback } from 'react';
import {
  Flex,
  Box,
  Divider,
  UseDisclosureProps,
  useDisclosure,
} from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { customReportActions } from '@/store/custom-report';
import { staticReportActions } from '@/store/static-report';
import HeaderActions from '@/pages/reports/custom-reports/show/headerActions';
import { TooltipText } from '@/ui/TooltipText';
import { KText } from '@/ui-library/components';
import { Link } from 'react-router-dom';
import { Edit05, LineChartUp03 } from '@kula-ai/icons-core';
import { LastRefreshedTime } from './components';
import { FiltersWrapper } from '@/pages/reports/custom-reports/show/FiltersWrapper';
import { ReportTypes } from '../../types';
import { REPORT_CONSTANTS } from '../../constants';
import { CreateEditCustomReportModal } from '@/pages/reports/custom-reports/components/CreateEditCustomReportModal';
import { useQueryClient } from '@tanstack/react-query';
import {
  getItem,
  setItem,
  useIsPreferencesLoaded,
} from '@/store/ServerStorage/serverStorage';
import { STORAGE_KEYS } from '@/utils/storageKeys';

interface ReportHeaderProps {
  reportData: ReportTypes.ReportMeta;
  isEditMode: boolean;
  hasOriginalTiles: boolean;
  setEditMode: (_isEditMode: boolean) => void;
  showAddBlockDisclosure: UseDisclosureProps;
  onFilterChange: (_filterType: string, _filterData: unknown) => void;
}

type NormalFilter = {
  filter_by?: { value?: unknown };
  filter_condition?: { value?: unknown };
  filter_by_values?: Array<{ id?: unknown }>;
};

type NormalizedFilter = {
  by: unknown;
  cond: unknown;
  ids: unknown[];
};

type StoredCustomReportFilters = {
  localFilters?: NormalFilter[];
  baselineSnapshot?: NormalizedFilter[];
};

// Sorted ids of one filter, so order doesn't affect equality.
const extractIds = (filter: NormalFilter): unknown[] => {
  if (!Array.isArray(filter?.filter_by_values)) return [];
  return filter.filter_by_values.map((v) => v?.id ?? null).sort();
};

// One filter's semantic identity — field, condition, ids.
const toIdentity = (filter: NormalFilter): NormalizedFilter => ({
  by: filter?.filter_by?.value ?? null,
  cond: filter?.filter_condition?.value ?? null,
  ids: extractIds(filter),
});

// Sort by field so reordered arrays produce the same snapshot.
const byField = (a: NormalizedFilter, b: NormalizedFilter) =>
  String(a.by).localeCompare(String(b.by));

// Filter array → semantic snapshot (label/order changes don't invalidate it).
const normalizeFilterSnapshot = (
  filters: NormalFilter[] | undefined
): NormalizedFilter[] => {
  if (!Array.isArray(filters)) return [];
  return filters.map(toIdentity).sort(byField);
};

// Structural compare — snapshots have stable key order.
const snapshotsEqual = (a: NormalizedFilter[], b: NormalizedFilter[]) =>
  JSON.stringify(a) === JSON.stringify(b);

// Filter array → { field: { condition: [ids] } } Redux payload.
const buildFilterPayload = (filters: NormalFilter[]) =>
  filters.reduce<Record<string, Record<string, unknown[]>>>(
    (acc, { filter_by, filter_condition, filter_by_values }) => {
      if (filter_by_values?.length) {
        acc[filter_by.value as string] = {
          [filter_condition.value as string]: filter_by_values.map(
            ({ id }) => id
          ),
        };
      }
      return acc;
    },
    {}
  );

// Static: bare array. Custom: array + snapshot of API state at apply time.
const buildStoredValue = (
  filters: NormalFilter[],
  isStaticReport: boolean,
  apiFilters: NormalFilter[] | undefined
): NormalFilter[] | StoredCustomReportFilters =>
  isStaticReport
    ? filters
    : {
        localFilters: filters,
        baselineSnapshot: normalizeFilterSnapshot(apiFilters),
      };

// Filters to apply, or null. Clears legacy/stale entries as a side effect.
const readStoredOverride = (
  storageKey: string,
  isStaticReport: boolean,
  apiFilters: NormalFilter[] | undefined
): NormalFilter[] | null => {
  const stored = getItem(storageKey);
  if (!stored) return null;

  if (isStaticReport) {
    return Array.isArray(stored) ? (stored as NormalFilter[]) : null;
  }

  // Legacy pre-snapshot raw-array entries — drop.
  if (Array.isArray(stored)) {
    setItem(storageKey, null);
    return null;
  }

  const { localFilters, baselineSnapshot } =
    stored as StoredCustomReportFilters;

  // API moved on (e.g. owner saved new defaults) — drop stale override.
  if (
    !snapshotsEqual(baselineSnapshot ?? [], normalizeFilterSnapshot(apiFilters))
  ) {
    setItem(storageKey, null);
    return null;
  }

  return localFilters ?? null;
};

export const ReportHeader: FC<ReportHeaderProps> = React.memo(
  ({
    reportData,
    isEditMode,
    setEditMode,
    hasOriginalTiles,
    showAddBlockDisclosure,
    onFilterChange,
  }) => {
    const [localTitle, setLocalTitle] = useState<string>('');
    const dispatch = useDispatch();
    const {
      layoutData: report,
      defaultFilters,
      title,
      type,
      filtersToShow,
      isOwner,
      canModifyBlocks,
      isCustomReport,
    } = reportData;

    const queryClient = useQueryClient();
    const isPreferencesLoaded = useIsPreferencesLoaded();

    const isStaticReport = type === 'static';
    // Match FiltersWrapper's pattern: dispatch into the slice the tile fetch
    // actually reads from, so saved-view rehydrate works for static reports too.
    const { applyFilter, applyTimeFrame, applyViewBy } = isStaticReport
      ? staticReportActions
      : customReportActions;

    // Filter state managed locally in header
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [isLocalFilterApplied, setIsLocalFilterApplied] = useState(false);

    const isLocalFilterAppliedRef = useRef(false);
    isLocalFilterAppliedRef.current = isLocalFilterApplied;

    const handleSetSelectedFilters = useCallback(
      (filters: unknown[]) => {
        setSelectedFilters(filters);

        // Persist only user-driven changes outside edit mode.
        if (isEditMode || !isLocalFilterAppliedRef.current) return;

        const storageKey = STORAGE_KEYS.getReportFiltersKey(
          reportData?.reportId
        );
        if (!storageKey) return;

        setItem(
          storageKey,
          buildStoredValue(
            filters as NormalFilter[],
            isStaticReport,
            defaultFilters?.normalFilters
          )
        );
      },
      [isEditMode, reportData?.reportId, isStaticReport, defaultFilters]
    );

    const showUpdateReportModal = useDisclosure();

    const reportTitle =
      localTitle || report?.name || report?.title || title || '';
    const showEditIcon = isOwner && reportTitle && isCustomReport; // Only show edit for custom reports

    const reportTitleRef = useRef(reportTitle);
    reportTitleRef.current = reportTitle;
    const reportRef = useRef(report);
    reportRef.current = report;
    const reportIdRef = useRef(reportData?.reportId);
    reportIdRef.current = reportData?.reportId;

    // Only run on actual unmount - no dependencies
    useEffect(() => {
      return () => {
        const reportId = reportIdRef.current;
        const latestReport = reportRef.current;
        const latestTitle = reportTitleRef.current;

        // on unmount, update the report name in the cache with latest one
        if (reportId && latestReport) {
          queryClient.setQueryData([`getCustomReports/${reportId}`], {
            data: { ...latestReport, name: latestTitle },
            status: 'success',
          });
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Derive local title: once report data arrives and no user edit has occurred,
    // sync it during render instead of via useEffect (avoids an extra render cycle).
    if (report?.name && !localTitle) {
      setLocalTitle(report.name);
    }

    // Listen for title updates from the modal
    useEffect(() => {
      const handleTitleUpdate = (event: CustomEvent) => {
        setLocalTitle(event.detail.newTitle);
      };

      const reportContainer = document.querySelector('[data-report-container]');
      if (reportContainer) {
        reportContainer.addEventListener(
          'title-updated',
          handleTitleUpdate as EventListener
        );
        return () => {
          reportContainer.removeEventListener(
            'title-updated',
            handleTitleUpdate as EventListener
          );
        };
      }
    }, []);

    // Rehydrate the per-user override after init; reset-on-edit owns state in edit mode.
    useEffect(() => {
      const storageKey = STORAGE_KEYS.getReportFiltersKey(reportData?.reportId);
      if (!isPreferencesLoaded || !storageKey || !isLocalFilterApplied) return;
      if (isEditMode) return;

      const localFilters = readStoredOverride(
        storageKey,
        isStaticReport,
        defaultFilters?.normalFilters
      );
      if (!Array.isArray(localFilters)) return;

      // Empty array = user Reset; honour it over API defaults.
      setSelectedFilters(localFilters);
      dispatch(applyFilter(buildFilterPayload(localFilters)));
    }, [
      isStaticReport,
      isPreferencesLoaded,
      reportData?.reportId,
      isLocalFilterApplied,
      isEditMode,
      dispatch,
      applyFilter,
      defaultFilters,
    ]);

    // Reset filters to defaultFilters when entering edit mode
    useEffect(() => {
      if (isEditMode && defaultFilters) {
        // Reset normal filters if different
        if (defaultFilters.normalFilters) {
          const currentFiltersString = JSON.stringify(selectedFilters);
          const defaultFiltersString = JSON.stringify(
            defaultFilters.normalFilters
          );

          if (currentFiltersString !== defaultFiltersString) {
            setSelectedFilters(defaultFilters.normalFilters);
          }
        }

        // Reset timeframe if different
        if (defaultFilters.timeframe) {
          dispatch(applyTimeFrame({ ...defaultFilters.timeframe }));
        }

        // Reset dateGroup if different
        if (defaultFilters.dateGroup) {
          const dateGroupId =
            typeof defaultFilters.dateGroup === 'object'
              ? defaultFilters.dateGroup.id
              : defaultFilters.dateGroup;

          if (dateGroupId) {
            dispatch(applyViewBy({ id: dateGroupId }));
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode, defaultFilters, dispatch, applyTimeFrame, applyViewBy]);

    const renderBreadcrumb = () => {
      return (
        <Link
          to={REPORT_CONSTANTS.COPY_CHANGES.BREADCRUMB_TO}
          style={{
            display: 'inline-block',
            width: 'fit-content',
            height: '20px',
          }}
        >
          <Flex alignItems="center" gap="6px" w="fit-content">
            <LineChartUp03 size={14} />
            <KText size="sm-regular" color="secondary-700" h="20px">
              {REPORT_CONSTANTS.COPY_CHANGES.BREADCRUMB_PATH}
            </KText>
          </Flex>
        </Link>
      );
    };

    const renderTitle = () => {
      return (
        <>
          <TooltipText
            stringLength={50}
            text={reportTitle}
            as="span"
            size="xl-medium"
            h="24px"
            color="secondary-900"
            variant="default"
          />
          {showEditIcon && (
            <Edit05
              size={16}
              className="!text-primary-900 cursor-pointer mt-0.5"
              onClick={showUpdateReportModal.onOpen}
            />
          )}
        </>
      );
    };

    const renderTags = () => {
      return (
        <Flex alignItems="center" gap="6px" pl="6px">
          {hasOriginalTiles && <LastRefreshedTime scope="reports" />}
          {isEditMode && (
            <Flex
              bg="pastel-6-light"
              p="4px 8px"
              alignItems="center"
              gap="6px"
              borderRadius="sm"
              flexShrink={0}
              w="fit-content"
              h="26px"
            >
              {/* <Edit05 size={16} className="!text-pastel-6dark" /> */}
              <KText size="sm-medium" color="pastel-6-dark" h="20px">
                You're in edit mode
              </KText>
            </Flex>
          )}
        </Flex>
      );
    };

    const getReportTypeForFilters = () => {
      const titleToReportType = {
        'Pipeline history': 'pipeline',
        Interviews: 'interviews',
        Offers: 'offers',
        Hires: 'hires',
        Rejections: 'rejections',
        Contacts: 'contacts',
        Jobs: 'jobs',
        EEO: 'eeo',
        'Recruiter performance': 'recruiter-performance',
        'Flow Health': 'flow-health',
        Requisitions: 'requisitions',
        'Current job pipeline': 'live_job_pipeline',
      };
      return titleToReportType[title] || 'pipeline';
    };

    return (
      <Box w="100%">
        {/* Header Section */}
        <Flex
          borderBottomWidth="1px"
          borderBottomColor="border-color"
          boxShadow="ats-shadow-3"
          justifyContent="center"
          p="12px 24px"
          w="100%"
          gap="3px"
          flexDir="column"
        >
          {/* Breadcrumb */}
          {renderBreadcrumb()}
          {/* Title, edit icon, last refreshed time and actions */}
          <Flex alignItems="center" justifyContent="space-between" w="100%">
            {/* left part */}
            <Flex alignItems="baseline" gap="6px" flex={1}>
              {renderTitle()}
              {renderTags()}
            </Flex>
            {/* right part */}
            {report?.id && (
              <HeaderActions
                customReport={report}
                initiateTileSelection={() => {
                  showAddBlockDisclosure?.onOpen();
                }}
                selectedFilters={selectedFilters}
                hasWriteAccess={canModifyBlocks}
                isOwner={isOwner}
                isStaticReport={isStaticReport}
                isEditMode={isEditMode}
                setEditMode={setEditMode}
              />
            )}
          </Flex>

          <Divider my="6px" />

          <FiltersWrapper
            selectedFilters={selectedFilters}
            setSelectedFilters={handleSetSelectedFilters}
            filtersToShow={filtersToShow as string[]}
            isLocalFilterApplied={isLocalFilterApplied}
            setIsLocalFilterApplied={setIsLocalFilterApplied}
            isStaticReport={isStaticReport}
            reportType={getReportTypeForFilters()}
            onFilterChange={isEditMode ? onFilterChange : undefined}
            defaultFilters={defaultFilters}
          />
        </Flex>

        {showUpdateReportModal.isOpen && (
          <CreateEditCustomReportModal
            type="edit"
            report={{ ...report, name: reportTitle }}
            onClose={showUpdateReportModal.onClose}
            submitButtonLabel="Update report"
            modalTitle={{
              headerText: 'Name of custom report',
              subHeaderText: `Give your report a unique name so it's easier to identify`,
            }}
            onSubmit={() => {
              showUpdateReportModal.onClose();
            }}
          />
        )}
      </Box>
    );
  }
);

ReportHeader.displayName = 'ReportHeader';
